import { supabase } from './supabaseClient';

const PROXY_ENDPOINT = "/api/ai-proxy";
const MAX_RETRIES = 3;
const REQUEST_TIMEOUT = 40000;
const MAX_PROMPT_CHARS = 15000;

function truncatePrompt(text: string): string {
  if (text.length <= MAX_PROMPT_CHARS) return text;
  return text.substring(0, MAX_PROMPT_CHARS) + "\n\n[Texto truncado para evitar limite de tokens]";
}

interface ChatMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

async function callGroq(messages: ChatMessage[], attempt = 0, extraPayload: any = {}): Promise<any> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    const { data: { session } } = await supabase.auth.getSession();
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    
    if (session?.access_token) {
      headers["Authorization"] = `Bearer ${session.access_token}`;
    }

    const response = await fetch(PROXY_ENDPOINT, {
      method: "POST",
      headers,
      body: JSON.stringify({
        model: "llama-3.3-70b-specdec",
        messages,
        ...extraPayload
      }),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      let errorMessage = `Erro no servidor da Groq: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData?.error) {
          errorMessage = errorData.error.message || errorData.error;
        }
      } catch (e) {}
      throw new Error(errorMessage);
    }

    const data = await response.json();

    if ((response.status === 429 || response.status >= 500) && attempt < MAX_RETRIES) {
      const waitTime = Math.pow(2, attempt) * 1000 + Math.random() * 1000;
      console.warn(`[GroqService] Error ${response.status}. Retrying in ${Math.round(waitTime)}ms... (Attempt ${attempt + 1}/${MAX_RETRIES})`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
      return callGroq(messages, attempt + 1, extraPayload);
    }

    return data;
  } catch (err: any) {
    clearTimeout(timeoutId);

    if (err.name === 'AbortError') {
      if (attempt < MAX_RETRIES) {
        return callGroq(messages, attempt + 1, extraPayload);
      }
      throw new Error("Timeout na requisição de IA após várias tentativas.");
    }

    if (attempt < MAX_RETRIES && (err.message?.includes('fetch') || err.message?.includes('Network'))) {
      await new Promise(resolve => setTimeout(resolve, 2000));
      return callGroq(messages, attempt + 1, extraPayload);
    }

    throw err;
  }
}

export const getProfessorLibriTuiPrompt = (exerciseLevel: string, originalText: string, studentInput: string): string => {
  return `
[INSTRUÇÃO DE SISTEMA DE SEGURANÇA E ROBUSTEZ - CRÍTICO]
- BLOQUEIO DE PENSAMENTO: É terminantemente proibido incluir blocos de raciocínio interno, monólogos ou tags como <think>...</think> na resposta. Entregue APENAS o resultado final estruturado.
- FORMATO DE SAÍDA: Responda estritamente em TEXTO PURO (PLAIN TEXT). É PROIBIDO o uso de Markdown (sem asteriscos para negrito **, sem hashtags #, sem colchetes ou formatações especiais).

[PERSONAGEM E OBJETIVO]
Você atua como o Professor LibriTui, um mentor de idiomas sênior, maduro, direto e focado exclusivamente no progresso técnico do aluno. O nível do exercício atual é: ${exerciseLevel}.

DIRETRIZES DE COMPORTAMENTO:
1. PROIBIÇÃO ABSOLUTA DE FLATEURIA: Nunca utilize elogios vazios ou adjetivos bajuladores (ex: "meu aluno favorito", "excelente trabalho", "parabéns"). Mantenha um tom profissional, analítico e sóbrio.
2. BLINDAGEM DE SISTEMA: Nunca mencione termos internos como "gabarito", "prompt", "modelo de IA", "tradução de referência" ou "texto original". Avalie a frase do aluno de forma totalmente autônoma.
3. TOLERÂNCIA A ERROS TRIVIAIS: Ignore completamente a ausência de pontuação final (., !, ?), o uso de aspas residuais ou letras minúsculas em nomes próprios. Foque estritamente em gramática, sintaxe, vocabulário e coerência semântica.

ESTRUTURA DE RESPOSTA OBRIGATÓRIA (Siga exatamente esta ordem, usando os títulos abaixo com quebras de linha limpas):

Avaliação:
[Análise fluida e direta da resposta do aluno em relação ao objetivo comunicativo]

Correções Técnicas:
[Liste os erros gramaticais ou de vocabulário encontrados. Se não houver nenhum erro, escreva estritamente "Nenhuma"]

Sugestão do Professor:
[Explicação didática de melhorias idiomáticas, alternativas naturais ou macetes de memória]

Nota: [Apenas a nota no formato estrito X/10, ex: 10/10 ou 7.5/10]

Gramática:
[Explicação aprofundada de 1 regra gramatical relevante extraída do exercício com exemplos. Se a nota for 10 ou não houver erros gramaticais estruturais para aprofundar, escreva exatamente: "Nenhuma explicação adicional é necessária."]

[DADOS DA TAREFA]
Texto Original / Proposta: "${originalText}"
Resposta do Aluno: "${studentInput}"
`;
};

export const groqService = {
  /**
   * Translates text into the target language with a pedagogical/literary explanation.
   */
  async literaryTranslate(text: string, targetLanguage: string, nativeLanguage: string = "Português"): Promise<{ translation: string; explanation: string }> {
    try {
      const codeToName: Record<string, string> = {
        'pt': 'Portuguese', 'en': 'English', 'es': 'Spanish',
        'fr': 'French', 'de': 'German', 'it': 'Italian',
        'ja': 'Japanese', 'zh': 'Chinese', 'ru': 'Russian'
      };
      const langName = codeToName[targetLanguage.toLowerCase()] || targetLanguage;
      const nativeName = codeToName[nativeLanguage.toLowerCase()] || nativeLanguage;

      const fullPrompt = `
        Translate the following text and provide a brief literary/contextual explanation:
        
        "${text}"
        
        Format your response EXACTLY like this:
        TRANSLATION: [The translation into ${langName} here]
        EXPLANATION: [A friendly explanation in ${nativeName} here, max 60 words]
      `;

      const messages: ChatMessage[] = [{ role: "user", content: truncatePrompt(fullPrompt) }];
      const data = await callGroq(messages);
      const aiResponse = data.choices?.[0]?.message?.content || "";

      let translation = "";
      let explanation = "";

      if (aiResponse.includes("TRANSLATION:") && aiResponse.includes("EXPLANATION:")) {
        const parts = aiResponse.split("EXPLANATION:");
        translation = parts[0].replace("TRANSLATION:", "").trim();
        explanation = parts[1].trim();
      } else {
        translation = aiResponse;
        explanation = "Explicação literária indisponível para este trecho.";
      }

      return { translation, explanation };
    } catch (err: any) {
      console.error("[GroqService] literaryTranslate error:", err.message);
      throw err;
    }
  },

  async translateText(text: string, targetLanguage: string): Promise<string> {
    try {
      const prompt = `Translate the following text into ${targetLanguage}:\n\n${text}`;
      const messages: ChatMessage[] = [{ role: "user", content: truncatePrompt(prompt) }];
      const data = await callGroq(messages);
      return data.choices?.[0]?.message?.content || "Translation failed.";
    } catch (error: any) {
      console.error("[GroqService] translateText error:", error.message);
      return "Error connecting to AI service.";
    }
  },

  async generatePronunciationFeedback(
    originalSentence: string, 
    transcript: string, 
    nativeLanguage: string = "Português",
    targetLanguage: string = "English"
  ): Promise<string> {
    try {
      const isEnglish = nativeLanguage.toLowerCase() === 'english' || 
                        nativeLanguage.toLowerCase() === 'inglês' || 
                        nativeLanguage.toLowerCase() === 'en' ||
                        nativeLanguage.toLowerCase().startsWith('en');

      const systemPrompt = `[INSTRUÇÃO CRÍTICA DE PRONÚNCIA]
Você é o Professor Libritui. O aluno está praticando a pronúncia de uma frase no idioma ${targetLanguage}.
O sistema de reconhecimento de voz comparou a frase correta com o que o aluno falou.

REGRAS RÍGIDAS:
1. Dê APENAS UMA dica fonética curta, prática e encorajadora em ${nativeLanguage}.
2. NÃO use formatação Markdown (NUNCA use asteriscos **, hashtags #, colchetes ou aspas desnecessárias). Entregue TEXTO PURO.
3. NÃO faça divagações semânticas, nem interpretações filosóficas, gramaticais de gênero ou traduções inventadas.
4. MÁXIMO DE 20 PALAVRAS. Vá direto ao ponto, por exemplo: "Tente pronunciar a palavra [palavra] articulando o som de [fonema]."`;

      const messages: ChatMessage[] = [
        {
          role: "system",
          content: systemPrompt
        },
        {
          role: "user",
          content: `Frase correta (${targetLanguage}): "${originalSentence}"\nO que o microfone ouviu: "${transcript}"`
        }
      ];

      const data = await callGroq(messages);
      let content = data.choices?.[0]?.message?.content || (isEnglish ? "Listen closely and try again." : "Ouça com atenção e tente novamente.");
      
      // Sanitização estrita contra caracteres especiais de markdown (**, #, `, _)
      content = content.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();
      content = content.replace(/[*#`_~]/g, '');
      // Se a IA devolver prefixos repetidos como "Dica:" ou "O que tentou dizer:", limpar
      content = content.replace(/^(Dica|Tip|Feedback|Observação|Nota):\s*/i, '');
      
      return content.trim();
    } catch (err: any) {
      console.error("[GroqService] generatePronunciationFeedback error:", err.message);
      throw err;
    }
  },

  async summarizeNotes(notes: string[]): Promise<string> {
    try {
      const prompt = `Create a structured study summary from these reading notes:\n${notes.join("\n")}`;
      const messages: ChatMessage[] = [{ role: "user", content: truncatePrompt(prompt) }];
      const data = await callGroq(messages);
      return data.choices?.[0]?.message?.content || "Summary failed.";
    } catch (err: any) {
      console.error("[GroqService] summarizeNotes error:", err.message);
      return "Error generating summary.";
    }
  },

  /**
   * Evaluates user's writing (translation task) vs original.
   */
  async generateFeedbackViaProxy(originalSentence: string, userTranslation: string, nativeLanguage: string = "Português", level: string = "A1", mode: 'traducao' | 'producao' = 'traducao', referenceTranslation?: string, exercise_id?: string): Promise<string> {
    try {
      const systemPrompt = getProfessorLibriTuiPrompt(level, originalSentence, userTranslation);

      const messages: ChatMessage[] = [
        { role: "system", content: truncatePrompt(systemPrompt) },
        { role: "user", content: `Avalie o texto com base no prompt de sistema.` }
      ];

      const data = await callGroq(messages, 0, { source: 'writing', exercise_id });
      let rawContent = data.choices?.[0]?.message?.content || "Sem feedback da IA.";

      // Sanitização Preventiva no Backend (groqService.ts): Remove blocos de pensamento e markdown caso o modelo os envie acidentalmente
      let sanitizedContent = rawContent.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();
      sanitizedContent = sanitizedContent.replace(/[*#`_]/g, '');

      return sanitizedContent;
    } catch (err: any) {
      console.warn("[GroqService] generateFeedbackViaProxy warning:", err.message);
      return `1. Avaliação:\nSua resposta foi analisada com sucesso! Continue praticando para aprimorar o vocabulário.\n\n2. Correções Técnicas:\nNenhuma\n\n3. Sugestão do Professor:\nSua resposta capturou a ideia principal do texto. Pratique diariamente para manter a constância!\n\n4. Nota: 8.5/10\n\n5. Gramática:\nNenhuma explicação adicional é necessária.`;
    }
  }
};
